import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  CircularProgress, 
  Paper,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { Line } from 'react-chartjs-2';
import { fetchMeterData } from '../features/utilityMeters/utilityMeterSlice';

// 儀表讀數卡片元件
const MeterCard = ({ title, icon, currentReading, unit, lastReading, historyData, costEstimate }) => {
  const [showHistory, setShowHistory] = useState(false);
  
  const getTrendIcon = () => {
    if (!lastReading) return <TrendingFlatIcon />;
    
    const difference = currentReading - lastReading;
    if (difference > 0) {
      return <TrendingUpIcon color="error" />;
    } else if (difference < 0) {
      return <TrendingDownIcon color="success" />;
    } else {
      return <TrendingFlatIcon color="info" />;
    }
  };
  
  const getUsageDifference = () => {
    if (!lastReading) return '0';
    
    const difference = currentReading - lastReading;
    return difference > 0 ? `+${difference.toFixed(2)}` : difference.toFixed(2);
  };
  
  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ mr: 1 }}>{icon}</Box>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 2 }}>
            <Typography variant="h4" component="div">
              {currentReading.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {unit}
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              {getTrendIcon()}
              <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                {getUsageDifference()}
              </Typography>
            </Box>
          </Box>
          
          <Box>
            <Chip 
              label={`估計費用: $${costEstimate.toFixed(2)}`} 
              variant="outlined" 
              size="small"
            />
            <Button 
              size="small" 
              sx={{ ml: 1 }} 
              onClick={() => setShowHistory(true)}
              startIcon={<CompareArrowsIcon />}
            >
              歷史
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      <Dialog open={showHistory} onClose={() => setShowHistory(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{title}消耗歷史</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 300, mt: 2 }}>
            {historyData && (
              <Line 
                data={{
                  labels: historyData.dates,
                  datasets: [
                    {
                      label: `${title} (${unit})`,
                      data: historyData.readings,
                      borderColor: 'rgba(75, 192, 192, 1)',
                      backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    },
                    {
                      label: `費用 ($)`,
                      data: historyData.costs,
                      borderColor: 'rgba(255, 99, 132, 1)',
                      backgroundColor: 'rgba(255, 99, 132, 0.2)',
                      yAxisID: 'y1',
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      title: {
                        display: true,
                        text: `用量 (${unit})`
                      }
                    },
                    y1: {
                      position: 'right',
                      title: {
                        display: true,
                        text: '費用 ($)'
                      },
                      grid: {
                        drawOnChartArea: false
                      }
                    }
                  }
                }}
              />
            )}
          </Box>
          
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>消耗記錄</Typography>
          <List dense>
            {historyData && historyData.dates.map((date, index) => (
              <ListItem key={date} divider>
                <ListItemText 
                  primary={date} 
                  secondary={`${historyData.readings[index]} ${unit}`} 
                />
                <ListItemSecondaryAction>
                  <Typography variant="body2" color="text.secondary">
                    ${historyData.costs[index].toFixed(2)}
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistory(false)}>關閉</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const UtilityMeters = () => {
  const [tabValue, setTabValue] = useState(0);
  const dispatch = useDispatch();
  const { meters = {}, loading, error } = useSelector(state => state.utilityMeters || {});
  const { userRole = 'resident', userUnit = { id: 'A-1201' } } = useSelector(state => state.auth || {});
  
  useEffect(() => {
    dispatch(fetchMeterData(userUnit?.id));
  }, [dispatch, userUnit]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Paper sx={{ p: 2, bgcolor: '#fff9c4' }}>
          <Typography color="error">
            無法載入公用設施數據: {error}
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  // 確保 meters 的子屬性是陣列且有值
  const electricityMeters = Array.isArray(meters?.electricity) ? meters.electricity : [];
  const waterMeters = Array.isArray(meters?.water) ? meters.water : [];
  const gasMeters = Array.isArray(meters?.gas) ? meters.gas : [];
  
  // 根據用戶角色過濾表計
  const isManagerOrAdmin = userRole === 'manager' || userRole === 'admin';
  
  // 使用準備好的陣列而不是直接使用 meters.filter
  let filteredElectricityMeters = [];
  let filteredWaterMeters = [];
  let filteredGasMeters = [];
  
  if (isManagerOrAdmin && tabValue === 1) {
    // 公共區域讀數 - 假設公共區域的 unitId 包含 "common"
    filteredElectricityMeters = electricityMeters.filter(m => m?.isCommonArea || m?.unitId?.includes('common'));
    filteredWaterMeters = waterMeters.filter(m => m?.isCommonArea || m?.unitId?.includes('common'));
    filteredGasMeters = gasMeters.filter(m => m?.isCommonArea || m?.unitId?.includes('common'));
  } else {
    // 單位讀數
    filteredElectricityMeters = electricityMeters.filter(m => m?.unitId === userUnit?.id);
    filteredWaterMeters = waterMeters.filter(m => m?.unitId === userUnit?.id);
    filteredGasMeters = gasMeters.filter(m => m?.unitId === userUnit?.id);
  }
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>公用設施讀數</Typography>
      
      {isManagerOrAdmin && (
        <Paper sx={{ mb: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <Tab label="單位讀數" />
            <Tab label="公共區域讀數" />
          </Tabs>
        </Paper>
      )}
      
      <Grid container spacing={3}>
        {filteredElectricityMeters.map(meter => (
          <Grid item xs={12} sm={6} md={4} key={meter?.id || 'electricity'}>
            <MeterCard 
              title="電力"
              icon={<ElectricBoltIcon color="warning" />}
              currentReading={meter?.lastReading || 0}
              unit="kWh"
              lastReading={meter?.previousReading || 0}
              historyData={meter?.history || { dates: [], readings: [], costs: [] }}
              costEstimate={meter?.amount || 0}
            />
          </Grid>
        ))}
        
        {filteredWaterMeters.map(meter => (
          <Grid item xs={12} sm={6} md={4} key={meter?.id || 'water'}>
            <MeterCard 
              title="水務"
              icon={<WaterDropIcon color="info" />}
              currentReading={meter?.lastReading || 0}
              unit="m³"
              lastReading={meter?.previousReading || 0}
              historyData={meter?.history || { dates: [], readings: [], costs: [] }}
              costEstimate={meter?.amount || 0}
            />
          </Grid>
        ))}
        
        {filteredGasMeters.map(meter => (
          <Grid item xs={12} sm={6} md={4} key={meter?.id || 'gas'}>
            <MeterCard 
              title="煤氣"
              icon={<LocalFireDepartmentIcon color="error" />}
              currentReading={meter?.lastReading || 0}
              unit="m³"
              lastReading={meter?.previousReading || 0}
              historyData={meter?.history || { dates: [], readings: [], costs: [] }}
              costEstimate={meter?.amount || 0}
            />
          </Grid>
        ))}
      </Grid>
      
      {filteredElectricityMeters.length === 0 && filteredWaterMeters.length === 0 && filteredGasMeters.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
          <Typography variant="body1" color="text.secondary">
            沒有找到公用設施讀數數據
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default UtilityMeters; 